import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { DatapointAutomationBlocker } from '@rossum/api-client/automationBlockers';
import {
  IconAlertTriangle,
  IconBolt,
  IconDatabase,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Stack,
  SvgIcon,
  Tooltip,
  tooltipClasses,
  Typography,
} from '@rossum/ui/material';
import { getUITypeFromSchema } from '../../../redux/modules/schema/helpers';
import { schemaMapSelector } from '../../../redux/modules/schema/schemaMapSelector';
import {
  AnyDatapointDataST,
  MatchedTriggerRuleDatapoint,
} from '../../../types/datapoints';
import { UiFieldType } from '../../../types/schema';
import { AutomationBlockersTooltip } from '../components/AutomationBlockersTooltip';
import {
  ValidationSourcesContent,
  ValidationSourcesIcon,
} from '../components/ValidationSourcesTooltip';
import { irrelevantTickInfo, isFieldEditableFormula } from '../helpers';
import { FormulaTooltip } from './components/FormulaTooltip';

type TooltipType = 'validationSources' | 'automationBlockers';

const validationSources: TooltipType = 'validationSources';
const blockers: TooltipType = 'automationBlockers';

type Props = {
  active?: boolean;
  data: AnyDatapointDataST;
  inFooter?: boolean;
  documentAutomated?: boolean;
  tickIconColor?: 'green' | 'white' | null;
  tooltipType: TooltipType | null;
  automationBlockers?: DatapointAutomationBlocker[];
  matchedRules?: MatchedTriggerRuleDatapoint;
  uiFieldType?: UiFieldType;
};

const getContentByType = ({
  data,
  documentAutomated,
  inFooter,
  tickIconColor,
  automationBlockers,
  active,
}: {
  data: AnyDatapointDataST;
  inFooter: boolean;
  documentAutomated?: boolean;
  tickIconColor?: 'green' | 'white' | undefined;
  automationBlockers?: DatapointAutomationBlocker[];
  active?: boolean;
}) => ({
  [validationSources]: {
    content: (
      <ValidationSourcesContent
        data={data}
        documentAutomated={documentAutomated}
      />
    ),
    icon: (
      <ValidationSourcesIcon
        inFooter={inFooter}
        tickIconColor={tickIconColor}
        data={data}
      />
    ),
    disabled:
      get(data, 'category') === 'multivalue' ||
      !get(data, 'validationSources', []).length,
  },
  [blockers]: {
    icon: (
      <SvgIcon fontSize="medium" color={active ? 'action' : 'error'}>
        <IconBolt />
      </SvgIcon>
    ),
    content: (
      <AutomationBlockersTooltip
        automationBlockers={automationBlockers || []}
        data={data}
      />
    ),
    disabled: false,
  },
});

type PrioritiesArgs = {
  displayAutomationBlockers: boolean;
  tickIconColor?: null | string;
  displaySources?: boolean;
  uiFieldType?: UiFieldType;
};

export const getTooltipTypeWithHighestPriority = ({
  displayAutomationBlockers,
  tickIconColor,
  displaySources,
  uiFieldType,
}: PrioritiesArgs): TooltipType | null => {
  if (displayAutomationBlockers) {
    return blockers;
  }

  if (
    uiFieldType === 'data' ||
    uiFieldType === 'formula' ||
    uiFieldType === 'manual'
  ) {
    return null;
  }

  if ((displaySources === undefined || displaySources) && tickIconColor) {
    return validationSources;
  }

  return null;
};

const DatapointTooltip = ({
  active,
  data,
  documentAutomated,
  inFooter,
  tickIconColor,
  tooltipType,
  automationBlockers,
  matchedRules,
  uiFieldType: datapointUiFieldType,
}: Props) => {
  const schemas = useSelector(schemaMapSelector);
  const simpleMultivalueChildrenSchemaId =
    data.category === 'multivalue' && data.meta.isSimpleMultivalue
      ? data.schema?.children?.[0]
      : undefined;
  // for simpleMultivalues, we need to get the UI type from the children schema
  const uiFieldType = simpleMultivalueChildrenSchemaId
    ? getUITypeFromSchema(schemas.get(simpleMultivalueChildrenSchemaId))
    : datapointUiFieldType;

  // PERF: O(n^2)!
  const hasBlockers =
    data.category === 'multivalue'
      ? data.meta.isSimpleMultivalue
        ? automationBlockers?.[0]?.samples?.length
        : automationBlockers?.length
      : automationBlockers?.some(
          blocker =>
            blocker?.samples?.some(sample => sample.datapointId === data.id) ??
            false
        );

  if (tooltipType === 'automationBlockers' && !hasBlockers) {
    return null;
  }

  if (uiFieldType === 'formula' && tooltipType !== 'automationBlockers') {
    return (
      <FormulaTooltip
        data={data}
        inFooter={inFooter}
        isEditable={!!isFieldEditableFormula(schemas.get(data.schemaId))}
      />
    );
  }

  if (
    !inFooter &&
    uiFieldType === 'lookup' &&
    tooltipType !== 'automationBlockers'
  ) {
    return (
      <SvgIcon fontSize="large">
        <IconDatabase />
      </SvgIcon>
    );
  }

  if (matchedRules && tooltipType !== 'automationBlockers')
    return (
      <SvgIcon fontSize="medium" color="warning">
        <IconAlertTriangle />
      </SvgIcon>
    );

  if (
    !data ||
    !tooltipType ||
    (tooltipType === 'validationSources' &&
      irrelevantTickInfo(
        get(data, 'validationSources', []),
        documentAutomated ?? false
      ))
  ) {
    return null;
  }

  const fieldLabel = get(data, ['schema', 'label'], '');
  const contentType = getContentByType({
    data,
    documentAutomated: documentAutomated ?? false,
    inFooter: inFooter ?? false,
    tickIconColor: tickIconColor ?? undefined,
    automationBlockers,
    active,
  })[tooltipType];

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            [`&.${tooltipClasses.tooltip}`]: {
              backgroundColor: t => t.palette.background.default,
              fontSize: t => t.typography.pxToRem(14),
              textAlign: 'left',
              p: 0,
              width: 400,
              maxWidth: 400,
            },
            [`& .${tooltipClasses.arrow}`]: {
              color: t => t.palette.background.default,
            },
          },
        },
      }}
      title={
        contentType.disabled ? (
          ''
        ) : (
          <Stack spacing={2} padding={2}>
            <Typography variant="body2">{fieldLabel}</Typography>
            {contentType.content}
          </Stack>
        )
      }
      placement={inFooter ? 'top' : 'right'}
      enterNextDelay={
        tooltipType === 'automationBlockers' ? 0 : inFooter ? 300 : 1000
      }
    >
      <Box
        sx={inFooter ? { position: 'absolute', top: 2, right: 2 } : undefined}
      >
        {contentType.icon}
      </Box>
    </Tooltip>
  );
};

export default DatapointTooltip;
